import { useLayoutEffect, useRef, useState } from 'react'
import { blocks } from '@w/content-blocks'
import { Doc, type Json } from '@w/crdt'
import { ReviewProvider, useReview } from './context'

export type { NestedCommentEditorProps as CommentComposerProps } from '../../nested-editor-ctx'
import type { NestedCommentEditorProps as CommentComposerProps } from '../../nested-editor-ctx'
import { EDITABLE } from '../dom-selection'

export function NestedCommentComposer({ focusOnMount, onChange, testId, value, compact, bordered = true, placeholder = 'Comment…', actions, onBlur }: CommentComposerProps) {
  const { renderEditor } = useReview()
  const [doc] = useState(() => new Doc({ content: { children: (value ? blocks.parse(value, { withChars: true })
    : [{ id: '', type: 'p', children: [] }]) as unknown as Json } }))
  const host = useRef<HTMLDivElement>(null)
  const [multiline, setMultiline] = useState(false)
  useLayoutEffect(() => {
    const editable = host.current?.querySelector<HTMLElement>(EDITABLE)
    if (!editable || !bordered || !compact) return () => {}
    const measure = () => {
      const style = getComputedStyle(editable)
      const pixels = (value: string) => Number(value.replace('px', ''))
      const padding = pixels(style.paddingTop) + pixels(style.paddingBottom)
      const lineHeight = pixels(style.lineHeight) || pixels(style.fontSize) * 1.2
      const singleLineHeight = Math.max(pixels(style.minHeight) || 0, lineHeight + padding)
      setMultiline(editable.scrollHeight > singleLineHeight + 1)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(editable)
    return () => { observer.disconnect() }
  }, [bordered, compact])
  useLayoutEffect(() => {
    const editable = host.current?.querySelector<HTMLElement>(EDITABLE)
    if (!editable) return
    editable.dataset.testid = testId
    editable.setAttribute('aria-label', 'Comment')
    if (focusOnMount) editable.focus()
  }, [focusOnMount, testId])
  const leftPadding = bordered && !multiline ? 'pl-0' : 'pl-2'
  return <ReviewProvider><div ref={host} data-review-comment-field onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) onBlur?.() }} className={compact ? `relative min-w-0 flex-1 rounded-lg ${bordered ? `border ${multiline ? 'border-separator focus-within:border-primary' : 'border-transparent'}` : ''}` : 'relative'}>
    {renderEditor?.({ doc, focusOnMount, testIds: { root: testId },
      className: compact ? `text-body-small min-h-8 max-h-40 max-w-none cursor-text overflow-y-auto ${leftPadding} py-1 pr-9 outline-none focus-visible:outline-none [&_.cb-paragraph]:my-0 [&_.cb-paragraph]:text-body-small` : 'text-body-medium max-h-64 min-h-24 max-w-none overflow-y-auto px-3 py-2 outline-none',
      placeholder: <span hidden />,
      onStateChange: state => { onChange(blocks.serialize(state.blocks)) } })}
    {!value && <span className={`text-on-surface-secondary pointer-events-none absolute top-0 left-0 ${compact ? `${leftPadding} py-1 text-body-small` : 'px-3 py-2'}`}>{placeholder}</span>}
    {compact ? <div className="absolute right-1 bottom-1 flex items-center gap-1">{actions}</div> : actions}
  </div></ReviewProvider>
}

export function NestedCodeSourceEditor({ language, onChange, value }: {
  language?: string; onChange: (source: string) => void; value: string
}) {
  const { renderEditor } = useReview()
  const [doc] = useState(() => new Doc({ content: { children: [{ id: '', type: 'pre', ...(language ? { lang: language } : {}),
    children: [{ id: '', type: 'chars', text: [{ id: '', text: value }] }],
  }] as unknown as Json } }))
  return <ReviewProvider>{renderEditor?.({ doc, className: 'text-body-small min-h-24 max-w-none outline-none', onStateChange: state => {
    const code = state.blocks[0]
    if (code?.type === 'pre') onChange(blocks.serialize(code.children, { plainText: true }))
  } })}</ReviewProvider>
}
